import React, { useMemo, ReactNode, useCallback } from 'react';
import Fuse from 'fuse.js';
import { xor } from 'lodash-es';
import { useLocationPicker } from '@frontend/location-picker';
import { WeaveLocationGroup, type WeaveLocation } from '@frontend/scope';
import { createContextWithSelectors as createContext, useContextSelector } from '@frontend/store';

interface SearchableEntity {
  id: string;
  name: string;
  type: 'organization' | 'group' | 'location';
  orgId: string;
  orgName: string;
  groupId?: string;
  groupName?: string;
  locationCount?: number;
  groupCount?: number;
  slug?: string;
}

export interface LocationGroupResult {
  groupId: string;
  groupName: string;
  orgId: string;
  orgName: string;
  locations: Array<{
    id: string;
    name: string;
  }>;
}

interface PrivilegedPickerContextValue {
  orgIds: string[];
  currentOrgData: Record<string, string[]>;
  currentOrgLocationGroups: string[];
  searchableEntities: SearchableEntity[];
  searchResults: {
    locationGroups: LocationGroupResult[];
    groups: SearchableEntity[];
    organizations: SearchableEntity[];
  };
  orgLocationGroups: Record<
    string,
    Array<{ id: string; name: string; numberOfLocations: number; locations: string[] }>
  >;
  scopeHierarchy: Record<string, Record<string, string[]>>;
  hasSearchResults: boolean;
  selectedOrgId: string;
  selectedGroupId: string;
  selectedLocationIds: string[];
  searchResultCount: {
    locations: number;
    locationGroups: number;
    organizations: number;
  };
  closeModal?: () => void;
  onApply?: () => Promise<void>;
  getLocationNames: (locationIds: string[]) => string;
  getScopeName: (scope: string, type?: 'location' | 'group' | 'org') => string;
  hasDifferentLocations: (selected: string[], inFlight: string[]) => boolean;
  hasSwitchedScope: boolean;
  renderOrgInfo?: (orgId: string) => JSX.Element | null;
  renderLocationInfo?: (locationId: string) => JSX.Element | null;
  renderBannerInfo?: () => JSX.Element | null;
  onApplyError?: (error: unknown) => void;
}

interface PrivilegedPickerProviderProps {
  children: ReactNode;
  selectedOrgId: string;
  selectedGroupId: string;
  selectedLocationIds: string[];
  scopeHierarchy: Record<string, Record<string, string[]>>;
  accessibleLocationData: Record<string, WeaveLocation>;
  accessibleGroupData: Record<string, WeaveLocationGroup>;
  onApply?: () => Promise<void>;
  getScopeName: (scope: string, type?: 'location' | 'group' | 'org') => string;
  closeModal?: () => void;
  renderOrgInfo?: (orgId: string) => JSX.Element | null;
  renderLocationInfo?: (locationId: string) => JSX.Element | null;
  renderBannerInfo?: () => JSX.Element | null;
  onApplyError?: (error: unknown) => void;
}

const PrivilegedPickerContext = createContext<PrivilegedPickerContextValue>(null!);

export const PrivilegedPickerProvider: React.FC<PrivilegedPickerProviderProps> = ({
  children,
  closeModal,
  renderOrgInfo,
  renderLocationInfo,
  renderBannerInfo,
  onApplyError,
  getScopeName,
  selectedOrgId,
  selectedGroupId,
  selectedLocationIds,
  scopeHierarchy,
  accessibleLocationData,
  accessibleGroupData,
  onApply,
}) => {
  const {
    organizationViewId,
    debouncedSearchTerm,
    activeSearchFilter,
    inFlightOrganizationId,
    inFlightGroupId,
    inFlightLocationIds,
    isSearchMode,
  } = useLocationPicker([
    'organizationViewId',
    'debouncedSearchTerm',
    'activeSearchFilter',
    'inFlightOrganizationId',
    'inFlightGroupId',
    'inFlightLocationIds',
    'isSearchMode',
  ]);

  const orgIds = useMemo(() => Object.keys(scopeHierarchy), [scopeHierarchy]);

  const currentOrgData = useMemo(() => {
    return organizationViewId && scopeHierarchy[organizationViewId] ? scopeHierarchy[organizationViewId] : {};
  }, [scopeHierarchy, organizationViewId]);

  const currentOrgLocationGroups = useMemo(() => Object.keys(currentOrgData), [currentOrgData]);

  const searchableEntities = useMemo(() => {
    const entities: SearchableEntity[] = [];

    Object.entries(scopeHierarchy).forEach(([orgId, groups]) => {
      const orgName = getScopeName(orgId, 'org');
      const groupCount = Object.keys(groups).length;
      let totalLocationCount = 0;

      Object.entries(groups).forEach(([groupId, locations]) => {
        const groupName = getScopeName(groupId, 'group');
        const groupData = accessibleGroupData[groupId];
        totalLocationCount += locations.length;

        entities.push({
          slug: groupData?.slug,
          id: groupId,
          name: groupName,
          type: 'group',
          orgId,
          orgName,
          locationCount: locations.length,
        });

        locations.forEach((locationId) => {
          const locationData = accessibleLocationData[locationId];

          entities.push({
            ...locationData,
            id: locationId,
            name: getScopeName(locationId, 'location'),
            type: 'location',
            orgId,
            orgName,
            groupId,
            groupName,
          });
        });
      });

      entities.push({
        id: orgId,
        name: orgName,
        type: 'organization',
        orgId,
        orgName,
        groupCount,
        locationCount: totalLocationCount,
      });
    });

    return entities;
  }, [scopeHierarchy, getScopeName, accessibleLocationData, accessibleGroupData]);

  const fuseInstance = useMemo(() => {
    const options: Fuse.IFuseOptions<SearchableEntity> = {
      keys: ['name'],
      threshold: 0.3,
      ignoreLocation: true,
    };
    return new Fuse(searchableEntities, options);
  }, [searchableEntities]);

  const searchResults = useMemo(() => {
    if (!isSearchMode || !debouncedSearchTerm) {
      return { locationGroups: [], groups: [], organizations: [] };
    }

    const exactMatches = searchableEntities.filter(
      (entity) => (entity.slug && entity.slug === debouncedSearchTerm) || entity.id === debouncedSearchTerm
    );

    let results = [];
    if (exactMatches.length > 0) {
      results = exactMatches.map((item) => ({ item }));
    } else {
      results = fuseInstance.search(debouncedSearchTerm);
    }

    const locationResults = results.filter((r) => r.item.type === 'location').map((r) => r.item);

    const locationGroups: Record<string, LocationGroupResult> = {};

    locationResults.forEach((location) => {
      const { groupId, groupName, orgId, orgName } = location;

      if (!groupId) return;

      if (!locationGroups[groupId]) {
        locationGroups[groupId] = {
          groupId,
          groupName: groupName || '',
          orgId,
          orgName,
          locations: [],
        };
      }

      locationGroups[groupId].locations.push({
        id: location.id,
        name: location.name,
      });
    });

    return {
      locationGroups: Object.values(locationGroups),
      groups: results.filter((r) => r.item.type === 'group').map((r) => r.item),
      organizations: results.filter((r) => r.item.type === 'organization').map((r) => r.item),
    };
  }, [debouncedSearchTerm, fuseInstance, isSearchMode, searchableEntities]);

  const orgLocationGroups = useMemo(() => {
    const result: Record<
      string,
      Array<{ id: string; name: string; numberOfLocations: number; locations: string[] }>
    > = {};

    Object.entries(scopeHierarchy).forEach(([orgId, groups]) => {
      result[orgId] = Object.entries(groups).map(([groupId, locations]) => ({
        id: groupId,
        name: getScopeName(groupId, 'group'),
        numberOfLocations: locations.length,
        locations: locations,
      }));
    });

    return result;
  }, [scopeHierarchy, getScopeName]);

  const hasSearchResults =
    (!!searchResults.locationGroups.length && activeSearchFilter === 'locations') ||
    (!!searchResults.groups.length && activeSearchFilter === 'locationGroups') ||
    (!!searchResults.organizations.length && activeSearchFilter === 'organizations');

  const searchResultCount = useMemo(
    () => ({
      locations: searchResults.locationGroups.reduce((acc, group) => acc + group.locations.length, 0) ?? 0,
      locationGroups: searchResults.groups.length ?? 0,
      organizations: searchResults.organizations.length ?? 0,
    }),
    [searchResults]
  );

  const getLocationNames = useCallback(
    (locationIds: string[]) =>
      locationIds.length > 1 ? `${locationIds.length} Locations` : getScopeName(locationIds?.[0], 'location'),
    [getScopeName]
  );

  const hasDifferentLocations = useCallback((selected: string[], inFlight: string[]) => {
    return xor(inFlight, selected).length > 0 ?? false;
  }, []);

  const hasSwitchedScope = useMemo(() => {
    return (
      inFlightOrganizationId !== (selectedOrgId ?? '') ||
      inFlightGroupId !== (selectedGroupId ?? '') ||
      hasDifferentLocations(selectedLocationIds, inFlightLocationIds)
    );
  }, [inFlightOrganizationId, inFlightGroupId, inFlightLocationIds, hasDifferentLocations]);

  const contextValue = useMemo(
    () => ({
      orgIds,
      currentOrgData,
      currentOrgLocationGroups,
      searchableEntities,
      searchResults,
      orgLocationGroups,
      hasSearchResults,
      searchResultCount,
      getLocationNames,
      hasDifferentLocations,
      hasSwitchedScope,
      getScopeName,
      scopeHierarchy,
      selectedOrgId,
      selectedGroupId,
      selectedLocationIds,
      closeModal,
      renderOrgInfo,
      renderLocationInfo,
      renderBannerInfo,
      onApplyError,
      onApply,
    }),
    [
      orgIds,
      currentOrgData,
      currentOrgLocationGroups,
      searchableEntities,
      searchResults,
      orgLocationGroups,
      hasSearchResults,
      searchResultCount,
      getLocationNames,
      hasDifferentLocations,
      hasSwitchedScope,
      getScopeName,
      scopeHierarchy,
      selectedOrgId,
      selectedGroupId,
      selectedLocationIds,
      closeModal,
      renderOrgInfo,
      renderLocationInfo,
      renderBannerInfo,
      onApplyError,
      onApply,
    ]
  );

  return <PrivilegedPickerContext.Provider value={contextValue}>{children}</PrivilegedPickerContext.Provider>;
};

export const usePrivilegedPicker = <T extends any>(selector: (state: PrivilegedPickerContextValue) => T) => {
  return useContextSelector(PrivilegedPickerContext, selector);
};
